/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import LinkButton from '@atlaskit/button/link';
import { cssMap as adsCssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@compiled/react';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { PlacementFrontend } from '@post-office/browser--growth/confluence-side-nav';
import { AdControls } from '@post-office/components--ad-controls';
import {
	type AnyFrontendStage,
	type FrontendPlacementMessage,
} from '@post-office/message-maker-browser/types';
import { type CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';
import { FormattedMessage, useIntl } from 'react-intl';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { type MessageTemplateContext } from '../../..';
import { registeredContent } from './content';

const styles = adsCssMap({
	cardSurface: {
		width: '100%',
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		padding: token('space.200'),
	},
	// Inline-block so a wrapped title flows under the icon, as in the design.
	cohortIcon: {
		display: 'inline-block',
		width: '20px',
		height: '20px',
		verticalAlign: 'text-bottom',
		marginInlineEnd: token('space.075'),
		borderRadius: token('radius.medium'),
	},
	assignedWorkIcon: {
		backgroundColor: token('color.background.accent.blue.subtle'),
	},
	activeTeamIcon: {
		backgroundColor: token('color.background.accent.lime.subtle'),
	},
});

// Compiled: the avatar ring (box-shadow) and overlap (negative margin) are not allowed by ADS cssMap.
const avatarStyles = cssMap({
	avatarStack: {
		display: 'inline-flex',
		verticalAlign: 'middle',
		marginInlineEnd: token('space.075'),
	},
	avatar: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		flexShrink: 0,
		borderRadius: token('radius.full'),
		// A ring instead of a border keeps the full 24px for the initials.
		boxShadow: `0 0 0 ${token('border.width.selected')} ${token('elevation.surface')}`,
		font: token('font.body.small'),
		fontWeight: token('font.weight.bold'),
		whiteSpace: 'nowrap',
	},
	overlap: {
		marginInlineStart: token('space.negative.050'),
	},
	// Avatar colours follow the design (purple, blue, yellow); each pair meets 4.5:1 contrast.
	avatarOne: {
		backgroundColor: token('color.background.accent.purple.bolder'),
		color: token('color.text.inverse'),
	},
	avatarTwo: {
		backgroundColor: token('color.background.accent.blue.bolder'),
		color: token('color.text.inverse'),
	},
	avatarThree: {
		backgroundColor: token('color.background.accent.yellow.subtle'),
		color: token('color.text'),
	},
});

// The body names at most this many collaborators, each with an avatar.
const MAX_COLLABORATORS = 3;

// Initials shown in the mocked avatar stack, e.g. "Priya Kapoor" -> "PK".
const getInitials = (fullName: string): string =>
	fullName
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? '')
		.join('');

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			cohort,
			jiraUrl,
			itemCount,
			collaborators,
			teamSpaceName,
			handleOnDismissed,
		}: MessageFrontendProps) => {
			const intl = useIntl();
			const { onCtaActioned } = usePostOfficeUserInteractions();

			const isAssignedWork = cohort === 'assigned-work';
			// The active-team title needs the space name; without it there is no card to show.
			if (!isAssignedWork && !teamSpaceName) {
				return null;
			}
			const visibleCollaborators = collaborators.slice(0, MAX_COLLABORATORS);
			const messageValues = {
				itemCount,
				collaboratorCount: visibleCollaborators.length,
				collaboratorNames: intl.formatList(visibleCollaborators),
				teamSpaceName,
			};
			const title = intl.formatMessage(
				isAssignedWork
					? registeredContent.default.assignedWorkTitle
					: registeredContent.default.activeTeamTitle,
				messageValues,
			);
			const body = intl.formatMessage(
				isAssignedWork
					? registeredContent.default.assignedWorkBody
					: registeredContent.default.activeTeamBody,
				messageValues,
			);
			const cta = intl.formatMessage(
				isAssignedWork
					? registeredContent.default.assignedWorkCta
					: registeredContent.default.activeTeamCta,
			);
			// AdControls records the decline and the lifecycle dismissal (which retires the
			// message) before calling this, so the template only hides the card.
			const handleAdControlsDismissed = () => {
				handleOnDismissed();
			};

			// Opening Jira retires the card for the rest of the experiment (click-check filter).
			const handleCtaClicked = () => {
				onCtaActioned({
					interactionIdentifier: 'c2j-open-jira-cta',
					destinationUrl: jiraUrl,
					additionalMetadata: { cohort },
				});
				handleOnDismissed();
			};

			return (
				<AnalyticsContext data={{ source: 'c2j-confluence-side-nav', cohort }}>
					<Box xcss={styles.cardSurface} backgroundColor="elevation.surface">
						<Stack space="space.100">
							<Inline spread="space-between" alignBlock="start" space="space.050">
								<Heading size="small">
									<span
										aria-hidden="true"
										css={[
											styles.cohortIcon,
											isAssignedWork ? styles.assignedWorkIcon : styles.activeTeamIcon,
										]}
									/>
									{title}
								</Heading>
								<AdControls
									iconSize="small"
									showWhyAmISeeingThis={true}
									whyAmISeeingThisModalContent={{
										title: <FormattedMessage {...registeredContent.default.whyTitle} />,
										message: <FormattedMessage {...registeredContent.default.whyBody} />,
										reasons: [],
									}}
									onDismissed={handleAdControlsDismissed}
								/>
							</Inline>
							<Text as="p" color="color.text.subtle">
								{!isAssignedWork &&
									visibleCollaborators.length > 0 && (
										// Decorative: initials only, the body text carries the message.
										<span aria-hidden="true" css={avatarStyles.avatarStack}>
											{visibleCollaborators.map((name, index) => (
												<span
													key={`${index}-${name}`}
													css={[
														avatarStyles.avatar,
														index === 0 && avatarStyles.avatarOne,
														index === 1 && avatarStyles.avatarTwo,
														index === 2 && avatarStyles.avatarThree,
														index > 0 && avatarStyles.overlap,
													]}
												>
													{getInitials(name)}
												</span>
											))}
										</span>
									)}
								{body}
							</Text>
							{/* Inline keeps the CTA content-width and left-aligned, as in the design. */}
							<Inline>
								<LinkButton href={jiraUrl} onClick={handleCtaClicked} appearance="primary">
									{cta}
								</LinkButton>
							</Inline>
						</Stack>
					</Box>
				</AnalyticsContext>
			);
		},
	},
});
